import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { useRouteTracking } from "@/hooks/useRouteTracking";
import { measureFID, measureLCP, preloadCriticalResources } from "@/utils/performance";
import PageFade from "@/components/PageFade";
import React from "react";
import Index from "./pages/Index";
import Admin from "./pages/Admin";
import Auth from "./pages/Auth";
import Listings from "./pages/Listings";
import MerchantDashboard from "./pages/MerchantDashboard";
import MerchantListings from "./pages/MerchantListings";
import MerchantLeads from "./pages/MerchantLeads";
import Marketplace from "./pages/Marketplace";
import Travel from "./pages/Travel";
import PackageBooking from "./pages/travel/PackageBooking";
import Flights from "./pages/travel/Flights";
import Stays from "./pages/travel/Stays";
import TravelSearch from "./pages/travel/TravelSearch";
import TravelResults from "./pages/travel/TravelResults";
import PassengerDetails from "./pages/travel/PassengerDetails";
import Checkout from "./pages/travel/Checkout";
import HotelSearch from "./pages/stays/HotelSearch";
import HotelResults from "./pages/stays/HotelResults";
import HotelCheckout from "./pages/stays/HotelCheckout";
import StaysConfirmed from "./pages/stays/StaysConfirmed";
import Education from "./pages/Education";
import CourseBooking from "./pages/education/CourseBooking";
import Healthcare from "./pages/Healthcare";
import TreatmentBooking from "./pages/healthcare/TreatmentBooking";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import Refund from "./pages/Refund";
import About from "./pages/About";
import Investors from "./pages/Investors";
import Contact from "./pages/Contact";
import Partners from "./pages/Partners";
import NotFound from "./pages/NotFound";
import BookingConfirmed from "./pages/BookingConfirmed";
// SEO Landing Pages
import PayMonthlyTravel from "./pages/PayMonthlyTravel";
import PayMonthlyEducation from "./pages/PayMonthlyEducation";
import PayMonthlyHealthcare from "./pages/PayMonthlyHealthcare";
import PointsDashboard from "./pages/PointsDashboard";
import { AccountLayout } from "./components/account/AccountLayout";
import Dashboard from "./pages/account/Dashboard";
import Bookings from "./pages/account/Bookings";
import Transactions from "./pages/account/Transactions";
import Membership from "./pages/account/Membership";

const queryClient = new QueryClient();

// Route tracking component
function RouteTracker() {
  useRouteTracking();
  return null;
}

const App = () => {
  // Initialize performance tracking + capture referral code from URL
  React.useEffect(() => {
    measureFID();
    measureLCP();
    preloadCriticalResources();

    // Capture ?ref= param for referral tracking
    const params = new URLSearchParams(window.location.search);
    const refCode = params.get("ref");
    if (refCode && refCode.startsWith("FXA-")) {
      sessionStorage.setItem("flexaura_referral_code", refCode);
    }
  }, []);

  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <RouteTracker />
            <PageFade>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/admin" element={<Admin />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/listings" element={<Listings />} />
                <Route path="/merchant" element={<MerchantDashboard />} />
                <Route path="/merchant/listings" element={<MerchantListings />} />
                <Route path="/merchant/leads" element={<MerchantLeads />} />
                <Route path="/marketplace" element={<Marketplace />} />
                
                {/* SEO Landing Pages */}
                <Route path="/pay-monthly-travel" element={<PayMonthlyTravel />} />
                <Route path="/pay-monthly-education" element={<PayMonthlyEducation />} />
                <Route path="/pay-monthly-healthcare" element={<PayMonthlyHealthcare />} />
                
                <Route path="/travel" element={<Travel />} />
                <Route path="/travel/search" element={<TravelSearch />} />
                <Route path="/travel/results" element={<TravelResults />} />
                <Route path="/travel/passenger-details" element={<PassengerDetails />} />
                <Route path="/travel/checkout" element={<Checkout />} />
                <Route path="/travel/confirmed" element={<BookingConfirmed />} />
                <Route path="/travel/package-booking/:packageId" element={<PackageBooking />} />
                <Route path="/travel/flights" element={<Flights />} />
                <Route path="/travel/stays" element={<Stays />} />
                <Route path="/stays/search" element={<HotelSearch />} />
                <Route path="/stays/results" element={<HotelResults />} />
                <Route path="/stays/checkout" element={<HotelCheckout />} />
                <Route path="/stays/confirmed" element={<StaysConfirmed />} />
                <Route path="/education" element={<Education />} />
                <Route path="/education/course/:courseId" element={<CourseBooking />} />
                <Route path="/healthcare" element={<Healthcare />} />
                <Route path="/healthcare/treatment/:treatmentId" element={<TreatmentBooking />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/refund" element={<Refund />} />
                <Route path="/about" element={<About />} />
                <Route path="/investors" element={<Investors />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/partners" element={<Partners />} />
                <Route path="/booking/confirmed" element={<BookingConfirmed />} />
                <Route path="/points" element={<PointsDashboard />} />
                <Route path="/account" element={<AccountLayout />}>
                  <Route path="dashboard" element={<Dashboard />} />
                  <Route path="bookings" element={<Bookings />} />
                  <Route path="transactions" element={<Transactions />} />
                  <Route path="membership" element={<Membership />} />
                  <Route path="points" element={<PointsDashboard />} />
                </Route>
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </PageFade>
          </BrowserRouter>
        </TooltipProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
};

export default App;
